/**
 * Type Utilities
 * 
 * This module contains utilities for working with data types and type checking.
 */

import { Column } from './types';

/**
 * Check if a value is a Float object from Apache Arrow
 */
export function isFloatObject(value: unknown): boolean {
  if (!value || typeof value !== 'object') {
    return false;
  }
  
  try {
    const anyValue = value as any;
    return 'value' in anyValue && typeof anyValue.value === 'number';
  } catch (err) {
    return false;
  }
}

/**
 * Format a schema type to a user-friendly string representation
 * 
 * @param schemaType - The schema type to format (can be a string, object, or undefined)
 * @returns A formatted string representation of the schema type
 */
export function formatSchemaType(schemaType: any): string {
  try {
    // Handle null/undefined
    if (schemaType == null) {
      return 'unknown';
    }
    
    // Handle objects (like Float, Int32, etc. from Apache Arrow)
    if (typeof schemaType === 'object') {
      // Check constructor name first
      const constructorName = schemaType.constructor?.name;
      if (constructorName && constructorName !== 'Object') {
        // Map common Arrow type constructor names to friendly names
        const typeMap: Record<string, string> = {
          'Float': 'Float',
          'Float32': 'Float32',
          'Float64': 'Float64',
          'Int': 'Integer',
          'Int32': 'Int32',
          'Int64': 'Int64',
          'Utf8': 'Text',
          'Bool': 'Boolean',
          'Date': 'Date',
          'Date32': 'Date',
          'Date64': 'Date',
          'Timestamp': 'Timestamp',
          'Time': 'Time',
          'Decimal': 'Decimal'
        };
        
        if (typeMap[constructorName]) {
          return typeMap[constructorName];
        }
        
        return constructorName;
      }
      
      // Try to access type property if it exists
      if ('type' in schemaType && schemaType.type != null) {
        return formatSchemaType(schemaType.type); // Recursive call with the type property
      }
      
      // Try toString method
      if (typeof schemaType.toString === 'function') {
        const str = schemaType.toString();
        if (str !== '[object Object]') return str;
      }
      
      // If it's a Float object
      if (isFloatObject(schemaType)) {
        return 'Float';
      }
      
      // JSON stringify as last resort for objects
      try {
        return JSON.stringify(schemaType);
      } catch (e) {
        // Silent catch
      }
    }
    
    // Handle strings - normalize to lowercase and then capitalize first letter
    if (typeof schemaType === 'string') {
      // Map common type strings to friendly names
      const typeMap: Record<string, string> = {
        'utf8': 'Text',
        'int32': 'Int32',
        'int64': 'Int64',
        'float32': 'Float32',
        'float64': 'Float64',
        'bool': 'Boolean',
        'boolean': 'Boolean',
        'date32': 'Date',
        'date64': 'Date',
        'timestamp': 'Timestamp',
        'time': 'Time',
        'decimal': 'Decimal',
        'varchar': 'Text',
        'char': 'Text',
        'string': 'Text',
        'text': 'Text',
        'integer': 'Integer',
        'double': 'Float64',
        'real': 'Float32'
      };
      
      const lowerType = schemaType.toLowerCase();
      if (typeMap[lowerType]) {
        return typeMap[lowerType];
      }
      
      // Capitalize first letter for other strings
      return schemaType.charAt(0).toUpperCase() + schemaType.slice(1).toLowerCase();
    }
    
    // Final fallback - convert to string
    return String(schemaType);
  } catch (err) {
    return 'unknown';
  }
} 